import { useEffect, useState } from "react";
import { api, API, fmtMoney, today, daysAgo } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, FileText, FileSpreadsheet } from "lucide-react";

export default function TrialBalance() {
    const [from, setFrom] = useState(daysAgo(180));
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/trial-balance", { params: { date_from: from, date_to: to } }).then(({ data }) => setData(data));
    }, [from, to]);

    const exportFile = async (format) => {
        const win = format === "pdf" ? window.open("", "_blank") : null;
        const token = localStorage.getItem("vyastra_token");
        const res = await fetch(`${API}/trial-balance/export?date_from=${from}&date_to=${to}&format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        if (format === "pdf") {
            if (win && !win.closed) win.location.href = blobUrl;
            else { const a = document.createElement("a"); a.href = blobUrl; a.target = "_blank"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); }
        } else {
            const ext = format === "xlsx" ? "xlsx" : "csv";
            const a = document.createElement("a");
            a.href = blobUrl; a.download = `TrialBalance_${from}_${to}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(blobUrl);
        }
    };

    return (
        <>
            <PageHeader kicker="ACCOUNTING" title="Trial Balance" subtitle="A snapshot of debit and credit balances across all accounts."
                actions={<>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[140px]" />
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[140px]" />
                    <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="tb-export-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                    <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="tb-export-xlsx"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
                    <Button variant="outline" size="sm" onClick={() => exportFile("csv")} data-testid="tb-export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
                </>} />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {data && (
                        <table className="fin-table">
                            <thead><tr><th>Code</th><th>Account</th><th>Type</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r.account_id}>
                                        <td className="font-mono text-[12px]">{r.code}</td>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="text-[12px] text-muted-foreground capitalize">{r.type.replace("_", " ")}</td>
                                        <td className="num">{r.debit ? fmtMoney(r.debit) : "—"}</td>
                                        <td className="num">{r.credit ? fmtMoney(r.credit) : "—"}</td>
                                    </tr>
                                ))}
                                <tr className="bg-muted/40 font-medium">
                                    <td colSpan={3}>Total</td>
                                    <td className="num">{fmtMoney(data.total_debit)}</td>
                                    <td className="num">{fmtMoney(data.total_credit)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </PageBody>
        </>
    );
}
